import { formatDistanceToNow } from 'date-fns';
import { Briefcase, Building2, FileText, Mail } from 'lucide-react-native';
import { useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { Badge, Text, VStack } from '../../../components/primitives';
import { BentoSurface } from '../../../components/primitives/BentoSurface';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import { colors } from '../../../theme/colors';
import { cardSurface } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import type { Lead } from '../../../types/leads';
import { getLeadCardContactName, getLeadCardDescription } from '../utils/leadCardFields';
import { getLeadDisplayName } from '../utils/leadDisplayName';
import { LeadSourceBadges } from './LeadSourceBadges';
import { LeadTagChips } from './LeadTagChips';

interface LeadListItemProps {
  lead: Lead;
  onPress?: (lead: Lead) => void;
  selected?: boolean;
  onSelect?: (lead: Lead) => void;
  showCheckbox?: boolean;
  /**
   * Number of columns in the grid this card is rendering inside. Defaults
   * to 1 (single-column list). Used on native to compute the card's per-cell
   * width — without this, the explicit `cardWidth` would span the full
   * viewport even when the parent FlatList is showing a 2- or 3-column grid.
   */
  numColumns?: number;
}

function InfoLine({
  icon: Icon,
  text,
  width,
  testID,
}: Readonly<{ icon: typeof Building2; text: string; width?: number; testID?: string }>) {
  return (
    <View
      testID={testID}
      style={[styles.infoLine, width != null ? { width } : styles.infoLineFlex]}
    >
      <Icon size={15} color={colors.text.tertiary} strokeWidth={1.7} />
      <Text
        variant="caption"
        color={colors.text.secondary}
        style={[styles.infoText, width != null ? { width: width - 19 } : styles.infoTextFlex]}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {text}
      </Text>
    </View>
  );
}

export function LeadListItem({
  lead,
  onPress,
  selected,
  onSelect,
  showCheckbox,
  numColumns = 1,
}: Readonly<LeadListItemProps>) {
  const { t, language } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const { width: windowWidth } = useWindowDimensions();

  // The layout chain (DashboardLayout pad + FlatList contentContainer pad)
  // should constrain the card width, but iOS Yoga doesn't reliably propagate
  // that constraint to a Pressable inside a FlatList row. We compute explicit
  // widths for the card AND its inner body — flex:1 + minWidth:0 isn't enough
  // on iOS when a child Text (the email line) has a wide intrinsic width.
  // On web, windowWidth is the full viewport (which is wider than the dashboard
  // content area), so applying these explicit widths overflows the container and
  // forces a horizontal scrollbar. Web uses flex sizing instead.
  // Native chrome = mobileContentContainer paddingHorizontal (xl = 24) on each
  // side = 48. Matching this keeps the card centered in its row instead of
  // leaving asymmetric slack against one edge.
  // When the parent FlatList is rendering a multi-column grid (tablet at
  // ≥780/1280px), divide the available width by numColumns and subtract the
  // inter-column gap (columnWrapperStyle.columnGap = tokens.spacing.md). Without
  // this the card spans the full viewport and overflows its grid cell — the
  // gridItem's overflow:hidden then hard-clips the title instead of letting
  // it ellipsize at the column boundary.
  const isNative = Platform.OS !== 'web';
  const checkboxOffset = showCheckbox ? 20 + tokens.spacing.md : 0;
  const cardWidth = (() => {
    if (!isNative) return undefined;
    const outerPad = 48;
    const interColumnGap = (numColumns - 1) * tokens.spacing.md;
    const perColumn = (windowWidth - outerPad - interColumnGap) / numColumns;
    return Math.max(perColumn, 280);
  })();
  const bodyWidth = isNative
    ? cardWidth! - tokens.spacing.md * 2 - 42 - tokens.spacing.md - checkboxOffset
    : undefined;

  const webHoverStyle = Platform.select<ViewStyle>({
    web: {
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      cursor: 'pointer',
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  const timeAgo = formatDistanceToNow(new Date(lead.createdAt), {
    addSuffix: true,
    locale: DATE_LOCALES[language],
  });

  const isActive = selected || isHovered;
  const displayName = getLeadDisplayName(lead);
  const initial = (displayName || '?')[0].toUpperCase();
  const title = displayName || t('common.fallbacks.unknown');
  const companyLine = getLeadCardContactName(lead);
  const description = getLeadCardDescription(lead);

  return (
    <Pressable
      testID={`leads_item_${lead.id}`}
      onPress={() => onPress?.(lead)}
      style={styles.pressable}
    >
      <View
        style={[
          styles.card,
          isNative ? { width: cardWidth } : styles.cardWeb,
          isActive && styles.cardActive,
          selected && styles.cardSelected,
          webHoverStyle,
        ]}
        {...hoverProps}
      >
        <BentoSurface radius={tokens.radius.lg} />
        <View style={styles.row}>
          {showCheckbox && (
            <Pressable onPress={() => onSelect?.(lead)} style={styles.checkboxWrapper}>
              <View
                style={[
                  styles.checkbox,
                  { borderColor: selected ? colors.primary[500] : colors.border.default },
                  selected && { backgroundColor: colors.primary[500] },
                ]}
              >
                {selected && <Text style={styles.checkmark}>✓</Text>}
              </View>
            </Pressable>
          )}

          <View style={styles.avatar}>
            <Text testID="lead_item_avatar_initial" style={styles.avatarLetter}>
              {initial}
            </Text>
          </View>

          <VStack
            spacing="xs"
            style={[styles.body, isNative ? { width: bodyWidth } : styles.bodyWeb]}
          >
            <View style={[styles.titleRow, isNative && { width: bodyWidth }]}>
              <Text
                testID="lead_item_title"
                variant="body"
                color={colors.text.primary}
                style={[
                  styles.title,
                  isNative ? { maxWidth: bodyWidth! - 96 - tokens.spacing.sm } : styles.titleWeb,
                ]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {title}
              </Text>
              <View style={styles.stagePill}>
                <View style={[styles.stageDot, { backgroundColor: lead.currentStage.color }]} />
                <Text style={styles.stageText} numberOfLines={1} ellipsizeMode="tail">
                  {lead.currentStage.name}
                </Text>
              </View>
            </View>

            <View
              testID="lead_item_info_stack"
              style={[styles.infoStack, isNative && { width: bodyWidth }]}
            >
              {companyLine && (
                <InfoLine
                  testID="lead_item_contact"
                  icon={Building2}
                  text={companyLine}
                  width={bodyWidth}
                />
              )}
              {lead.jobTitle && (
                <InfoLine icon={Briefcase} text={lead.jobTitle} width={bodyWidth} />
              )}
              {lead.email && <InfoLine icon={Mail} text={lead.email} width={bodyWidth} />}
            </View>

            {description && (
              <View
                testID="lead_item_description"
                style={[styles.descriptionRow, isNative && { width: bodyWidth }]}
              >
                <FileText size={15} color={colors.text.tertiary} strokeWidth={1.7} />
                <Text
                  variant="caption"
                  color={colors.text.secondary}
                  style={[
                    styles.infoText,
                    bodyWidth != null ? { width: bodyWidth - 19 } : styles.infoTextFlex,
                  ]}
                  numberOfLines={2}
                  ellipsizeMode="tail"
                >
                  {description}
                </Text>
              </View>
            )}

            <View style={[styles.footer, isNative && { width: bodyWidth }]}>
              <View style={styles.footerLeft}>
                <LeadSourceBadges lead={lead} showLabel={false} />
                {lead.qualificationStatus === 'pending_validation' && (
                  <Badge variant="subtle" color="amber" size="sm">
                    {t('leads.detail.qualification.pending')}
                  </Badge>
                )}
                {lead.qualificationStatus === 'not_qualified_title' && (
                  <Badge variant="subtle" color="red" size="sm">
                    {t('leads.detail.qualification.notQualified')}
                  </Badge>
                )}
              </View>
              <Text style={styles.timestamp} numberOfLines={1} ellipsizeMode="tail">
                {timeAgo}
              </Text>
            </View>

            {lead.tags.length > 0 && (
              <View style={[styles.tagsRow, isNative && { width: bodyWidth }]}>
                <LeadTagChips tags={lead.tags} />
              </View>
            )}
          </VStack>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  // iOS needs alignSelf:'stretch' to fill the FlatList row width — width:'100%'
  // doesn't propagate cleanly through Yoga in nested FlatList rows.
  // Web is the opposite: Pressable doesn't always honor alignSelf:'stretch'
  // as a flex item, so we set width:'100%' explicitly. Without this, the card
  // resolves against intrinsic content width inside a grid cell and overflows.
  pressable: {
    flex: 1,
    alignSelf: 'stretch',
    ...(Platform.OS === 'web' ? ({ width: '100%' } as ViewStyle) : {}),
  },
  card: {
    ...cardSurface(),
    flex: 1,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.md,
    overflow: 'hidden',
  },
  cardWeb: {
    width: '100%',
    maxWidth: '100%',
  },
  cardActive: {
    borderColor: colors.primary.border,
  },
  cardSelected: {
    backgroundColor: colors.primary.muted,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.md,
  },
  checkboxWrapper: {
    paddingTop: 6,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkmark: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.background.primary,
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    color: colors.primary.neon,
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 18,
  },
  body: {
    minWidth: 0,
  },
  // body sits in a flex ROW next to the avatar — width:'100%' would overflow
  // by the avatar width and push the stage pill off the card (which has
  // overflow:hidden). flex:1 + minWidth:0 lets it take ALL remaining row width
  // while still shrinking text children correctly.
  bodyWeb: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontWeight: '700',
    fontSize: 17,
    lineHeight: 22,
    letterSpacing: -0.3,
    marginRight: tokens.spacing.sm,
  },
  titleWeb: {
    flexShrink: 1,
    minWidth: 0,
  },
  stagePill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: colors.surface.row,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    maxWidth: 96,
    flexShrink: 0,
  },
  stageDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    marginRight: 6,
  },
  stageText: {
    color: colors.text.secondary,
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.2,
  },
  // Reserve the height of the three possible info lines (company, role,
  // email) so cards with missing data stay the same height as full ones.
  infoStack: {
    marginTop: 2,
    minHeight: (tokens.fontSize.xs * tokens.lineHeight.normal + 4) * 3,
  },
  infoLine: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 2,
  },
  infoLineFlex: {
    width: '100%',
    minWidth: 0,
  },
  infoText: {
    fontSize: 13,
    marginLeft: 6,
  },
  infoTextFlex: {
    flexShrink: 1,
    minWidth: 0,
    flex: 1,
  },
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 2,
    marginTop: 2,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 6,
    minHeight: 36,
    gap: tokens.spacing.xs,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    flexWrap: 'wrap',
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  tagsRow: {
    marginTop: 6,
  },
  timestamp: {
    fontSize: 13,
    color: colors.text.tertiary,
    marginLeft: tokens.spacing.sm,
    flexShrink: 0,
    maxWidth: 110,
  },
});
